import React, { useState, useContext, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

// Analytics
import { trackEvent } from 'analytics/trackFunctions'

// Contexts and Constants
import { signUpTabs, storageKey } from 'constants/storage'
import { UserContext } from 'contexts/user'
import { MeContext } from 'contexts/me'
import { BLOCKED_ACCOUNT_MSG } from 'constants/wording'
import { ZENDESK_URL } from 'config'

// Utils
import getIdentityType from 'utils/signIn'
import storage from 'utils/storage'
import handleNotification from 'utils/toast/handleNotification'

// Api
import { acceptTerms, getTokenFromSocialCode, signinGoogle } from 'api/auth'

// Components
import AppBannerToast from 'components/AppBannerToast'
import PrimaryButton from 'components/primaryButton'
import ModalContainer from 'components/ModalContainer'
import TermsConditionsContent from 'components/termsConditionsContent/termsConditionsContent'
import SocialButton from 'components/socialButton'
import PepModal from 'components/PepModal/PepModal'
import TextButton from 'components/textButton'
import Separator from './components/separator'
import TabSignIn from './components/tabSignIn'
import MainPanel from './components/mainPanel'
import Logo from './components/logo'

export default function SignIn() {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [selectedTab, setSelectedTab] = useState(signUpTabs.EMAIL)
  const [searchParams] = useSearchParams()

  const methods = useForm({
    defaultValues: {
      cellphone: '',
      email: '',
      password: ''
    }
  })

  const { login, refreshUserToken, setUser } = useContext(UserContext)
  const [loading, setLoading] = useState({
    loadingF: false,
    loadingG: false,
    loadingOthers: false
  })
  const navigate = useNavigate()
  const {
    me,
    showTC,
    setShowTc,
    showPEP,
    setShowPEP,
    isLoadingMe,
    setIsLoadingMe
  } = useContext(MeContext)
  const [termsIdentifier, setTermsIdentifier] = useState()
  const { errorToast } = handleNotification()

  const onGoogleClick = async () => {
    trackEvent({ eventName: 'bille_login_boton_google' })
    setLoading((prevState) => ({ ...prevState, loadingG: true }))
    const { data } = await signinGoogle()
    storage.set('session', 'google')
    const { link } = data?.result
    window.location.href = link
    setLoading((prevState) => ({ ...prevState, loadingG: false }))
  }

  async function onSubmit(data) {
    if (!executeRecaptcha) {
      return
    }

    trackEvent({ eventName: 'bille_login_boton_ingresar' })
    setLoading((prevState) => ({ ...prevState, loadingOthers: true }))

    const recaptchaToken = await executeRecaptcha('wallet_login')
    const params = getIdentityType(selectedTab, data)

    login(params, recaptchaToken).then(() => {
      storage.set(storageKey.EMAIL, params.identification)
      setLoading((prevState) => ({ ...prevState, loadingOthers: false }))
    })
  }

  useEffect(() => {
    const token = searchParams.get('code')
    const type = storage.get('session')
    const socialLogin = `${window.location.origin}`

    if (token && type === 'google') {
      setLoading({
        loadingF: false,
        loadingG: true,
        loadingOthers: false
      })
      getTokenFromSocialCode(token, socialLogin, 'google')
        .then((data) => {
          if (!data.success) {
            if (data.error?.errors?.blocked) {
              errorToast(BLOCKED_ACCOUNT_MSG)
              return
            }
            errorToast('Error obteniendo la información de Google')
            trackEvent({ eventName: 'bille_login_google_error' })
          }
          storage.set(storageKey.USER, data.data || null)
          setUser(data.data)
        })
        .catch(() => {
          errorToast('Error obteniendo la información de Google')
          trackEvent({ eventName: 'bille_login_google_error' })
        })
        .finally(() =>
          setLoading({ loadingF: false, loadingG: false, loadingOthers: false })
        )
    }

    trackEvent({ eventName: 'bille_pantalla_login' })
  }, [])

  useEffect(() => {
    if (me) {
      navigate('/home')
      setIsLoadingMe(false)
    }
  }, [me])

  async function onAcceptTerms() {
    try {
      setLoading((prevState) => ({
        ...prevState,
        loadingOthers: true
      }))
      setShowTc(false)

      await acceptTerms({
        application: 'WALLET',
        identifier: termsIdentifier
      })

      await refreshUserToken()
    } catch (error) {
      console.error('Error accepting terms or refreshing token:', error)
    } finally {
      setLoading((prevState) => ({
        ...prevState,
        loadingOthers: false
      }))
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className='bg-purple-900 relative flex overflow-x-hidden justify-between items-center h-screen flex-col md:flex-row'>
          <AppBannerToast />
          <MainPanel />
          <div className='flex rounded-t-[1.125rem] pt-6 md:pt-0 bg-gray-100 h-full w-screen md:w-1/2 md:rounded-none md:items-center justify-center'>
            <div className='flex flex-col justify-start px-3 w-full h-full md:px-[10%] md:justify-between md:items-center flex-grow'>
              <Logo />
              <div className='w-full h-full 2xl:mt-8 min-h-max'>
                <p className='text-black font-bold md:text-center md:text-lg md:mt-2'>
                  Accedé a tu billetera:
                </p>
                <SocialButton
                  className='my-6'
                  loading={loading.loadingG}
                  type='GOOGLE'
                  action={onGoogleClick}
                  buttonText='Ingresá con Google'
                />
                <Separator />
                <TabSignIn
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                />

                <section className='mt-auto md:mt-2'>
                  <hr className='border-solid my-2 bg-gray-500 border' />
                  <div className='flex justify-end my-6'>
                    <TextButton
                      handleFunction={() =>
                        window.location.assign(
                          `${process.env.REACT_APP_URL_LOGIN}/recuperar-contrasena?product=WALLET&backUrl=${window.location.origin}`
                        )
                      }
                      className='p-0'
                    >
                      No recuerdo mi contraseña
                    </TextButton>
                  </div>
                  <div className='flex justify-center md:my-0'>
                    <PrimaryButton
                      buttonText='Ingresar'
                      primaryButtonFunction={() => {}}
                      type='submit'
                      isDisabled={loading.loadingOthers || isLoadingMe}
                      isLoading={loading.loadingOthers || isLoadingMe}
                      className='md:max-w-[21rem]'
                    />
                  </div>
                  <div className='flex justify-center mt-6'>
                    <TextButton
                      className='text-xs text-purple-900'
                      handleFunction={() => {
                        window
                          .open(`${ZENDESK_URL}/requests/new`, '_blank')
                          .focus()
                      }}
                    >
                      Necesito ayuda
                    </TextButton>
                  </div>
                </section>
              </div>
              <div
                id='captcha-disclaimer'
                className='text-2xs text-black mt-3 mb-6 md:mb-2'
              >
                Este sitio está protegido por reCAPTCHA y se aplicarán{' '}
                <a
                  className='font-bold'
                  href='https://policies.google.com/privacy'
                >
                  la Política de privacidad
                </a>{' '}
                y{' '}
                <a
                  className='font-bold '
                  href='https://policies.google.com/terms'
                >
                  las Condiciones de servicio
                </a>{' '}
                de Google.
              </div>
              <span className='hidden md:block text-center text-gray-600 text-xs'>
                v. {process.env.REACT_APP_VERSION}
              </span>
            </div>
          </div>
        </div>
      </form>

      <ModalContainer
        title='Actualizamos nuestros términos y condiciones'
        isShown={showTC}
        setModalFunction={setShowTc}
        className='!max-w-564 !max-h-[35rem]'
      >
        <TermsConditionsContent isModal onTermsLoaded={setTermsIdentifier} />
        <div className='border-t border-solid border-gray-500 mb-6 mt-3' />
        <div className='flex mt-auto justify-between gap-12'>
          <PrimaryButton
            buttonText='Cancelar'
            primaryButtonFunction={() => setShowTc(false)}
            outlined
          />
          <PrimaryButton
            buttonText='Aceptar'
            primaryButtonFunction={() => onAcceptTerms()}
          />
        </div>
      </ModalContainer>

      <PepModal
        isOpen={showPEP}
        setIsOpen={setShowPEP}
        setLoadingPageBtn={setLoading}
        cancelButtonAction={() => setShowPEP(false)}
      />
    </FormProvider>
  )
}
